import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Dossier } from '@sc/data-access-models';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { AuthService } from '@sc/feature-auth-okta';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { DeclarationConditions } from '@sc/utils';
import { combineLatest, map, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InsurerRouterService {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly featureFlipService: FeatureFlippingManagerService,
    private readonly declarationConditions: DeclarationConditions,
    private readonly dossierSandbox: DossierSandboxService
  ) {}

  goToPreviousPage(previousUrl?: string) {
    let usedUrl = previousUrl;
    if (!usedUrl) {
      usedUrl = this.router.parseUrl(this.router.url)?.queryParams?.previousUrl;
    }

    return this.router.navigateByUrl(usedUrl);
  }

  getRedirectRoute() {
    return combineLatest([
      this.authService.userIsFitterB2B$(),
      this.authService.userIsInsurer$(),
      this.authService.userHasInsurerDashboardAccess$(),
      this.authService.userHasClaimsListAccess$(),
      this.authService.userHasInsurerAdminAccess$(),
      this.authService.userHasInsurerPartnerFitterAccess$(),
      this.authService.userIsFitter$(),
    ]).pipe(
      take(1),
      map(
        ([
          isFitterB2B,
          isInsurer,
          hasDashboardAccess,
          hasClaimListAccess,
          hasAdminAccess,
          hasPartnerListAccess,
          isFitter,
        ]) => {
          if (isFitterB2B) {
            return '/dossiers-search';
          } else if (isInsurer) {
            return '/declaration/customer-data';
          } else if (hasDashboardAccess) {
            return '/dashboard';
          } else if (hasClaimListAccess) {
            return '/dossiers-search';
          } else if (hasAdminAccess) {
            return '/administration';
          } else if (hasPartnerListAccess) {
            return '/partners';
          } else if (isFitter) {
            return '/folder/summary';
          } else {
            return null;
          }
        }
      )
    );
  }

  getRedirectRouteByDossier(dossierNumber) {
    this.dossierSandbox
      .fetchDossier(dossierNumber)
      ?.pipe(take(1))
      .subscribe((result) => {
        const url = this.getRedirectUrlFromDossier(result?.dossier as Dossier);

        return this.router.navigateByUrl(url);
      });
  }

  getRedirectUrlFromDossier(dossier: Dossier): string {
    let url = '/declaration/customer-data';
    if (!dossier) {
      return url;
    }
    const hasRedirectFeature = this.featureFlipService.isFeatureActivated(
      'b2b-acces-declaration-steps-by-dossier'
    );

    if (hasRedirectFeature && !dossier?.claim?.operationType) {
      this.dossierSandbox.updateDossierOperation(dossier);
    }

    const isGlassChoiceFilled =
      this.declarationConditions.isGlassChoiceFilled(dossier);
    const isCustomerInfosFilled =
      this.declarationConditions.isCustomerInfosFilled(dossier);
    const isDossierComplete =
      this.declarationConditions?.isDossierComplete(dossier);

    if (
      isGlassChoiceFilled &&
      isCustomerInfosFilled &&
      !isDossierComplete &&
      hasRedirectFeature
    ) {
      url = '/declaration/intervention';
    } else if (
      !isGlassChoiceFilled &&
      isCustomerInfosFilled &&
      !isDossierComplete &&
      hasRedirectFeature
    ) {
      url = '/declaration/sinister-data';
    } else if (!isDossierComplete || dossier?.state === 'ABANDONED') {
      url = '/declaration/declaration-end';
    }

    return url;
  }
}
